/* eslint-disable no-underscore-dangle,no-const-assign */
import { get, isUndefined, each, find, has, forEach, maxBy, isNil, isEmpty, indexOf, map } from 'lodash';
import { i18n } from 'vue-infra';
import ConversationsModel from '@core/models/ConversationsModel';
import BaseClient from '@core/common/BaseClient';
import { transformRequestQueryParams } from '@core/common/transformers/transform-request-query-params';
import {
  CONVERSATION_QUEUE_STATES,
  CONVERSATION_STATUSES,
  RESOURCES_CONFIGURATION_INTERVAL as INTERVAL,
  DIALOG_TYPES,
  METRIC_NAMES,
} from '@config/Consts';
import { getApiTimeParams } from '@core/utils/utils';

const SURVEY_TYPES = {
  POST_SURVEY: 'PostSurvey',
};

const CONVERSATION_STATUS_API_MAP = {
  IN_QUEUE: [CONVERSATION_STATUSES.OPEN],
  // Assigned Conversations
  OPEN: [CONVERSATION_STATUSES.OPEN],
  CLOSE: [CONVERSATION_STATUSES.CLOSE],
  DEFAULT: [CONVERSATION_STATUSES.OPEN, CONVERSATION_STATUSES.CLOSE],
};

const CONVERSATION_QUEUE_STATE_API_MAP = {
  IN_QUEUE: CONVERSATION_QUEUE_STATES.IN_QUEUE,
  // Assigned Conversations
  OPEN: CONVERSATION_QUEUE_STATES.ACTIVE,
};

function _extractNps(conversationSurveys) {
  let nps = null;
  const postSurvey = find(conversationSurveys, survey =>
    survey.surveyType === SURVEY_TYPES.POST_SURVEY);
  if (postSurvey && postSurvey.surveyData && postSurvey.surveyData.length > 0) {
    const npsQuestion = find(postSurvey.surveyData,
      question => question.questionType === 'nps' && question.answerId !== 'InvalidAnswer');
    nps = (has(npsQuestion, 'answer') &&
      !isNaN(npsQuestion.answer)) ? (parseInt(npsQuestion.answer, 10)) : null;
  }
  return nps;
}

function _getRequiredQueueState(agentGroupIds, agentIds, status) {
  // If the manager didn't filter by agent name or agent group or did filter but chose
  // to watch just open or closed conversations, we should get from API conversations with the
  // specific 'latestConversationQueueState' as mentioned in CONVERSATION_QUEUE_STATE_API_MAP
  // else (if manager filtered by agent name or agent group
  // and chose to watch all types of conversations
  // or just in queue conversations), we shouldn't get from API conversations with 'IN_QUEUE' value
  // for 'latestConversationQueueState'
  // This is needed in order to avoid the following situation:
  // manager filtered by agent name or agent group
  // and conversations that were transferred from this agent or group to queue are shown
  let requiredQueueState;
  if ((agentGroupIds === undefined && agentIds === undefined) || (status === 'OPEN' || status === 'CLOSE')) {
    requiredQueueState = CONVERSATION_QUEUE_STATE_API_MAP[status];
  } else if (status === null) {
    requiredQueueState = CONVERSATION_QUEUE_STATES.ACTIVE;
  } else if (status === 'IN_QUEUE') {
    requiredQueueState = '';
  }
  return requiredQueueState;
}

function _isConsumerNameValid(name) {
  // see AE-12141
  return name && name !== '*** LP deleted data ***';
}

function _extractConsumerNameBySDEs(sdesEvents) {
  let extractedName = '';
  let serverTimestamp = -1;
  each(sdesEvents, (event) => {
    let firstName = get(event, 'personalInfo.personalInfo.name');
    let lastName = get(event, 'personalInfo.personalInfo.surname');
    firstName = _isConsumerNameValid(firstName) ? firstName : '';
    lastName = _isConsumerNameValid(lastName) ? lastName : '';
    if ((firstName || lastName) && event.serverTimeStamp > serverTimestamp) {
      serverTimestamp = event.serverTimeStamp;
      extractedName = `${firstName} ${lastName}`;
      extractedName = extractedName.trim();
    }
  });

  return extractedName;
}

function _getMainDialog(dialogs) {
  return find(dialogs, dialog => dialog.dialogType === DIALOG_TYPES.MAIN);
}

function _getLatestConsumerData(consumersArray) {
  return maxBy(consumersArray, consumer => consumer.timeL);
}

function _getConsumerData(record) {
  const consumerParticipants = {};
  forEach(record.consumerParticipants, (consumerParticipant) => {
    consumerParticipants[consumerParticipant.dialogId] =
      consumerParticipants[consumerParticipant.dialogId] || {};
    if (consumerParticipant.timeL > get(consumerParticipants, [consumerParticipant.dialogId, consumerParticipant.participantId, 'timeL'], 0)) {
      const consumerName = _extractConsumerNameBySDEs(get(record, 'sdes.events')) || consumerParticipant.consumerName;
      consumerParticipants[consumerParticipant.dialogId][consumerParticipant.participantId] = {
        id: consumerParticipant.participantId,
        fullName: consumerName,
        timeL: consumerParticipant.timeL,
      };
    }
  });

  const mainDialog = _getMainDialog(record.dialogs);
  let consumerData = null;
  if (!isNil(mainDialog) &&
    !isEmpty(consumerParticipants) &&
    !isNil(consumerParticipants[mainDialog.dialogId])) {
    consumerData = _getLatestConsumerData(Object.values(consumerParticipants[mainDialog.dialogId]));
  }
  return consumerData;
}

// If the conversation status is in Queue or the value we receive from the server is 'NA',
// we should display 'N/A'
function _shouldDisplayNa(conversationStatus, value) {
  return conversationStatus === CONVERSATION_STATUSES.IN_QUEUE || value === 'NA' || !value;
}

// Modify the unique recognized conversation intents -
// set the firstIntent to be first element in the array
function _modifyIntentsArray(firstIntent, uniqueIntents) {
  let intentsArray = [];
  if (uniqueIntents && uniqueIntents.length > 0) {
    const firstIntentIndex =
      indexOf(map(uniqueIntents, 'intentName'), firstIntent.intentName);
    if (firstIntentIndex > 0) {
      const firstIntentElement = uniqueIntents.splice(firstIntentIndex, 1);
      uniqueIntents.unshift(firstIntentElement[0]);
    }
    intentsArray = uniqueIntents;
  } else if (firstIntent) {
    intentsArray.push(firstIntent);
  }
  return intentsArray;
}

function _getCloseReason(recordInfo) {
  // UNKNOWN, CONSUMER, AGENT, TIMEOUT
  if (!recordInfo.closeReason || recordInfo.status !== CONVERSATION_STATUSES.CLOSE) {
    return ' ';
  }
  return i18n.t(`amd:conversation_closed_by_${recordInfo.closeReason.toLowerCase()}`);
}

function transformConversationsRequest(settings) {
  const params = {};
  const queryParams = {};

  params.contentToRetrieve = ['consumerParticipants', 'sdes', 'responseTime', 'conversationSurveys', 'dialogs'];

  if (settings.getters.hasLiveIntent) {
    params.contentToRetrieve.push('uniqueIntents');
  }

  if (settings.filter.agentIds && settings.filter.agentIds.length > 0) {
    params.latestAgentIds = settings.filter.agentIds;
  }
  if (settings.filter.groupIds && settings.filter.groupIds.length > 0) {
    params.latestAgentGroupIds = settings.filter.groupIds;
  }
  if (settings.filter.skills && settings.filter.skills.length > 0) {
    params.latestSkillIds = settings.filter.skills;
  }
  if (settings.filter.liveIntentIds && settings.filter.liveIntentIds.length > 0) {
    params.intentName = settings.filter.liveIntentIds;
  }

  params.status = CONVERSATION_STATUS_API_MAP.DEFAULT;
  if (settings.filter.status) {
    params.status = CONVERSATION_STATUS_API_MAP[settings.filter.status];
  }

  const queueStateParam = _getRequiredQueueState(
    params.latestAgentGroupIds,
    params.latestAgentIds,
    settings.filter.status);
  if (queueStateParam !== undefined) {
    params.latestConversationQueueState = queueStateParam;
  }

  if (settings.filter.timeframe) {
    const apiTimeParams = getApiTimeParams(settings.filter.timeframe);
    params.start = { from: apiTimeParams.from, to: apiTimeParams.to };
    params.technicalEnd = { from: apiTimeParams.lastUpdateFrom };
  }
  if (settings.getters.hasContactCenterFeature) {
    queryParams.rollover = 'true';
  }
  return {
    config: {
      params: transformRequestQueryParams(queryParams, settings),
    },
    body: params,
    interval: INTERVAL.SEARCH_ENGAGEMENTS,
    method: BaseClient.HTTP_METHODS.POST,
  };
}

function transformConversationsResponse(settings, response) {
  let data = null;
  const searchEngagementsRecords = get(response, 'searchEngagementsResponse.data.conversationHistoryRecords');
  if (!isUndefined(searchEngagementsRecords)) {
    data = [];
    // We execute getBlockConsumerName getter here as we want the most updated value
    const blockConsumerName = settings.getters.getBlockConsumerName;
    each(searchEngagementsRecords, (record) => {
      const consumerData = blockConsumerName ? null : _getConsumerData(record);
      const consumerName = consumerData ? consumerData.fullName : i18n.t('amd:conversations_unauthenticated_visitor');

      // NOTE: latestQueueState is not being updated after conversation is being closed.
      // So here we check first if the conversation is closed.
      let conversationStatus;
      if (record.info) {
        if (record.info.status === CONVERSATION_STATUSES.CLOSE) {
          conversationStatus = CONVERSATION_STATUSES.CLOSE;
        } else if (record.info.latestQueueState === CONVERSATION_STATUSES.IN_QUEUE) {
          conversationStatus = CONVERSATION_STATUSES.IN_QUEUE;
        }
      }
      if (!conversationStatus) {
        conversationStatus = CONVERSATION_STATUSES.OPEN;
      }
      const latestAgentFullName = record.info && record.info.latestAgentFullName;
      const latestAgentGroupName = record.info && record.info.latestAgentGroupName;
      const currentAgentName = _shouldDisplayNa(conversationStatus, latestAgentFullName) ? i18n.t('amd:missing_value_na') : latestAgentFullName;
      const currentAgentGroup = _shouldDisplayNa(conversationStatus, latestAgentGroupName) ? i18n.t('amd:missing_value_na') : latestAgentGroupName;
      const brandId = record.info && record.info.brandId;
      const currentBrandName = (record.info && record.info.brandName) || brandId || '';
      const intentsArray = _modifyIntentsArray(
        {
          intentName: record.info && record.info.firstIntentName,
          intentLabel: record.info && record.info.firstIntentLabel,
        },
        record && record.uniqueIntents,
      );
      const conversationRecord = new ConversationsModel({
        [METRIC_NAMES.AGENT_ID]:
        record.info && record.info.latestAgentId,
        [METRIC_NAMES.AGENT_NAME]:
        currentAgentName,
        [METRIC_NAMES.AGENT_GROUP]:
        currentAgentGroup,
        [METRIC_NAMES.VISITOR_NAME]:
        consumerName,
        [METRIC_NAMES.CONVERSATION_STATUS]:
        conversationStatus,
        [METRIC_NAMES.CONVERSATIONS_RESPONSE_TIME]:
        record.responseTime && record.responseTime.latestEffectiveResponseDueTime,
        [METRIC_NAMES.CONVERSATION_SKILL]:
        record.info && record.info.latestSkillName,
        [METRIC_NAMES.MCS]:
        record.info && record.info.mcs,
        // duration: record.info && record.info.duration, // Remove duration
        [METRIC_NAMES.CONVERSATION_ID]:
        record.info && record.info.conversationId,
        [METRIC_NAMES.CONSUMER_ID]:
        record.consumerParticipants && record.consumerParticipants.length > 0 &&
        record.consumerParticipants[0].participantId,
        [METRIC_NAMES.CSAT]:
          record.info && record.info.csatRate === undefined ? null : record.info.csatRate,
        [METRIC_NAMES.NPS]:
          _extractNps(record.conversationSurveys),
        [METRIC_NAMES.CONVERSATION_START_TIME]:
        record.info && record.info.startTimeL,
        [METRIC_NAMES.BRAND_NAME]:
        currentBrandName,
        [METRIC_NAMES.CONVERSATION_INTENTS]: intentsArray,
        [METRIC_NAMES.DIALOGS]: record.dialogs,
        [METRIC_NAMES.CLOSE_REASON]: _getCloseReason(record.info),
      });
      data.push(conversationRecord);
    });
  }
  const conversationsCount = get(response, 'searchEngagementsResponse.data._metadata.count');
  return { conversationsCount, data };
}

export default {
  transformConversationsRequest,
  transformConversationsResponse,
};
